import { isAxiosError, type AxiosInstance } from 'axios';

export interface AlbWafCatalogEntry {
  value: string;
  description: string;
  approved: boolean;
}

export interface AlbWafConfiguration {
  tag_key: string;
  revision: string;
  public_default: string;
  private_default: string;
  catalog: AlbWafCatalogEntry[];
}

export interface AlbWafObservedResult {
  timestamp: string;
  accountId: string;
  region: string;
  resourceId: string;
  decision: string;
  reason: string;
  executionMode: string;
  configRevision: string;
}

export type AlbWafRole = 'admin' | 'viewer';

function observedResult(entry: unknown): AlbWafObservedResult | null {
  if (!entry || typeof entry !== 'object') return null;
  const record = entry as Record<string, unknown>;
  const raw = record.raw && typeof record.raw === 'object'
    ? record.raw as Record<string, unknown>
    : {};
  const message = raw['@message'];
  let payload: Record<string, unknown> = raw;
  if (typeof message === 'string') {
    try {
      const parsed: unknown = JSON.parse(message);
      if (parsed && typeof parsed === 'object') payload = { ...raw, ...parsed as Record<string, unknown> };
    } catch {
      return null;
    }
  }

  if (payload.guardrail !== 'alb-waf' || payload.action !== 'alb_waf_decision') return null;
  const accountId = payload.account_id;
  const region = payload.region;
  const resourceId = payload.resource_id;
  const decision = payload.decision;
  const reason = payload.reason;
  const executionMode = payload.execution_mode;
  const configRevision = payload.config_revision;
  const timestamp = record.timestamp ?? raw['@timestamp'] ?? payload.timestamp;
  if ([accountId, region, resourceId, decision, reason, executionMode, configRevision, timestamp]
    .some((value) => typeof value !== 'string' || !value.trim())) return null;
  if (!Number.isFinite(Date.parse(timestamp as string))) return null;

  return {
    timestamp: timestamp as string,
    accountId: accountId as string,
    region: region as string,
    resourceId: resourceId as string,
    decision: decision as string,
    reason: reason as string,
    executionMode: executionMode as string,
    configRevision: configRevision as string,
  };
}

export async function loadAlbWafObservedResults(
  client: AxiosInstance, signal?: AbortSignal, now = new Date(),
): Promise<AlbWafObservedResult[]> {
  const to = now.toISOString();
  const from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  const response = await client.get<unknown>('/logs', {
    params: { from, to, guardrail: 'alb-waf', action: 'alb_waf_decision', log_type: 'execution' },
    signal,
  });
  if (!response.data || typeof response.data !== 'object'
    || !Array.isArray((response.data as { logs?: unknown }).logs)) {
    throw new Error('Resposta de logs ALB/WAF inv\u00e1lida.');
  }

  const latestByResource = new Map<string, AlbWafObservedResult>();
  for (const entry of (response.data as { logs: unknown[] }).logs) {
    const result = observedResult(entry);
    if (!result) continue;
    const key = `${result.accountId}/${result.region}/${result.resourceId}`;
    const current = latestByResource.get(key);
    if (!current || Date.parse(result.timestamp) > Date.parse(current.timestamp)) {
      latestByResource.set(key, result);
    }
  }
  return [...latestByResource.values()].sort(
    (left, right) => Date.parse(right.timestamp) - Date.parse(left.timestamp),
  );
}

export function validateAlbWafCatalog(catalog: AlbWafCatalogEntry[]): string | null {
  if (!Array.isArray(catalog)) return 'Revise o valor, a descri\u00e7\u00e3o e a aprova\u00e7\u00e3o das pol\u00edticas.';
  const values = new Set<string>();
  for (const entry of catalog) {
    if (!entry || typeof entry.value !== 'string' || !entry.value.trim()
      || entry.value !== entry.value.trim() || [...entry.value].length > 256
      || typeof entry.description !== 'string' || typeof entry.approved !== 'boolean') {
      return 'Revise o valor, a descri\u00e7\u00e3o e a aprova\u00e7\u00e3o das pol\u00edticas.';
    }
    if (values.has(entry.value)) return 'O cat\u00e1logo possui valores duplicados.';
    values.add(entry.value);
  }
  return null;
}

export function validateAlbWafDraft(configuration: AlbWafConfiguration): string | null {
  if (!Array.isArray(configuration.catalog) || configuration.catalog.length === 0) {
    return 'O cat\u00e1logo precisa conter valores aprovados.';
  }
  const catalogProblem = validateAlbWafCatalog(configuration.catalog);
  if (catalogProblem) return catalogProblem;
  const approved = new Set(configuration.catalog.filter((entry) => entry.approved).map((entry) => entry.value));
  if (!approved.has(configuration.public_default) || !approved.has(configuration.private_default)) {
    return 'Os dois padr\u00f5es precisam ser pol\u00edticas aprovadas do cat\u00e1logo.';
  }
  return null;
}

function checkedConfiguration(value: unknown): AlbWafConfiguration {
  if (!value || typeof value !== 'object') throw new Error('Resposta de configura\u00e7\u00e3o inv\u00e1lida.');
  const configuration = value as AlbWafConfiguration;
  if (configuration.tag_key !== 'waf-policy' || typeof configuration.revision !== 'string'
    || !configuration.revision.trim() || typeof configuration.public_default !== 'string'
    || typeof configuration.private_default !== 'string' || validateAlbWafDraft(configuration)) {
    throw new Error('Resposta de configura\u00e7\u00e3o inv\u00e1lida.');
  }
  return configuration;
}

export async function loadAlbWafConfiguration(
  client: AxiosInstance, signal?: AbortSignal,
): Promise<AlbWafConfiguration> {
  const response = await client.get<unknown>('/alb-waf/configuration', { signal });
  return checkedConfiguration(response.data);
}

export async function loadAlbWafRole(client: AxiosInstance, signal?: AbortSignal): Promise<AlbWafRole> {
  const response = await client.get<{ role?: unknown }>('/me', { signal });
  if (response.data?.role !== 'admin' && response.data?.role !== 'viewer') {
    throw new Error('N\u00e3o foi poss\u00edvel confirmar o perfil de acesso.');
  }
  return response.data.role;
}

export async function saveAlbWafConfiguration(
  client: AxiosInstance, configuration: AlbWafConfiguration, signal?: AbortSignal,
): Promise<AlbWafConfiguration> {
  const creating = configuration.revision === '';
  if (creating) {
    if (configuration.tag_key !== 'waf-policy' || validateAlbWafDraft(configuration)) {
      throw new Error('Configuração inicial inválida.');
    }
  } else {
    checkedConfiguration(configuration);
  }
  const response = await client.put<unknown>('/alb-waf/configuration', {
    expected_revision: creating ? null : configuration.revision,
    public_default: configuration.public_default,
    private_default: configuration.private_default,
    catalog: configuration.catalog.map(({ value, description, approved }) => ({ value, description, approved })),
  }, { signal });
  return checkedConfiguration(response.data);
}

export function albWafErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) return error.response?.status;
  if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }
  return undefined;
}